import type {LitigationCase} from './litigationLedger.js';
import {applyLitigationPlanFieldValue, litigationCloseModeOptions, litigationFieldRuleKindInfo, litigationInternalNoBasisReady, litigationPlanFieldValue, litigationTrialStageOptions, resolveLitigationFieldRule, type LitigationFieldRuleOverrides} from './litigationLedgerPlan.js';
import {upsertPartyRoleName} from './litigationLedger.js';

export const LITIGATION_EDIT_PATCH_KEYS = ['caseName', 'title', 'caseNature', 'courtNo', 'courtNumbers', 'relatedCourtNo', 'venues', 'venue', 'handler', 'counsel', 'counselOrg', 'claimSummary', 'counterclaimSummary', 'resultSummary', 'counterclaimResult', 'nonMonetaryResult', 'awardedAmount', 'counterclaimAwardedAmount', 'filingDate', 'amount', 'principalAmount', 'counterclaimAmount', 'currency', 'disputeType', 'instance', 'position', 'ourEntity', 'parties', 'closed', 'litigationClosedAt', 'closeMode', 'enforcementStatus', 'unfilledFields'] as const;

export function blankLitigationBusinessFields(): Partial<LitigationCase> {
  return {caseName: '', title: '', caseNature: '', ourEntity: '', parties: [], filingDate: '', handler: '', currency: '', nodes: [], unfilledFields: ['caseNature', 'disputeType', 'instance', 'position', 'enforcementStatus', 'currentStage', 'currency']};
}

export function litigationFieldChoices(fieldId: string, overrides?: LitigationFieldRuleOverrides): string[] {
  return [...new Set((resolveLitigationFieldRule(fieldId, overrides).allowed || '').split(/\s*[\/／、,，;；\n|]\s*/).map((value) => value.trim()).filter(Boolean))];
}

/** Convert a displayed cell value to the API patch, preserving enum and party semantics. */
export function litigationFieldEditPatch(item: LitigationCase, fieldId: string, raw: string, overrides?: LitigationFieldRuleOverrides): Record<string, unknown> {
  const rule = resolveLitigationFieldRule(fieldId, overrides);
  if (litigationFieldRuleKindInfo(rule.ruleKind)?.readOnly) throw new Error('此字段由系统生成，不能手动修改');
  const value = raw.trim();
  if (rule.ruleKind === 'choice' && value) {
    const typed = fieldId === 'currentStage'
      ? litigationTrialStageOptions(item)
      : fieldId === 'closeMode'
        ? litigationCloseModeOptions(item)
        : [];
    const allowed = typed.length ? typed : litigationFieldChoices(fieldId, overrides);
    if (!allowed.includes(value)) throw new Error('请从后台规定的选项中选择');
  }
  if (['filingDate', 'closedAt'].includes(fieldId) && value && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) throw new Error('请输入有效日期');
  if (['amount', 'principalAmount', 'counterclaimAmount'].includes(fieldId) && value && (!/^\d+(\.\d+)?$/.test(value.replace(/,/g, '').replace(/元/g, '')) || !Number.isFinite(Number(value.replace(/,/g, '').replace(/元/g, ''))))) throw new Error('金额必须为非负数字，单位为元');
  if (!value && item.unfilledFields?.includes(fieldId)) return {};
  if (fieldId === 'currentStage') return {stageName: value, ...(item.unfilledFields ? {unfilledFields: item.unfilledFields.filter((field) => field !== fieldId)} : {})};
  const next = structuredClone(item);
  applyLitigationPlanFieldValue(next, fieldId, value);
  if (item.unfilledFields && value) next.unfilledFields = item.unfilledFields.filter((field) => field !== fieldId);
  // The spreadsheet importer skips blank cells; interactive edits must support clearing them.
  if (fieldId === 'ourParty') {
    next.ourEntity = value;
    const party = next.parties.find((entry) => entry.role === 'our');
    if (party) party.name = value;
  }
  if (fieldId === 'opponent' || fieldId === 'plaintiffParty' || fieldId === 'thirdParty') {
    const role = fieldId === 'plaintiffParty' ? 'plaintiff' : fieldId === 'thirdParty' ? 'third' : 'opponent';
    if (!value) next.parties = upsertPartyRoleName(next.parties, role, '', `party_${role}_${item.id || 'row'}`);
  }
  if (fieldId === 'amount' && !value) next.amount = 0;
  if (fieldId === 'principalAmount' && !value) next.principalAmount = 0;
  if (fieldId === 'counterclaimAmount' && !value) next.counterclaimAmount = 0;
  if (fieldId === 'closedAt') next.litigationClosedAt = value;
  const patch: Record<string, unknown> = {};
  for (const key of LITIGATION_EDIT_PATCH_KEYS) {
    if (JSON.stringify(item[key]) !== JSON.stringify(next[key])) patch[key] = next[key];
  }
  // Explicitly selecting a choice must save even when it equals the internal fallback.
  const sourceKey: Record<string, string> = {closedFlag: 'closed', ourParty: 'ourEntity', opponent: 'parties', plaintiffParty: 'parties', thirdParty: 'parties', closedAt: 'litigationClosedAt'};
  if (value && item.unfilledFields?.includes(fieldId)) {
    const key = sourceKey[fieldId] || fieldId;
    patch[key] = next[key as keyof LitigationCase];
  }
  return patch;
}

export function litigationEditableCellValue(item: LitigationCase, fieldId: string): string {
  return fieldId === 'amount' ? String(item.amount || '') : String(litigationPlanFieldValue(item, fieldId) ?? '');
}

/**
 * 合并 Excel 与侧车的未填写标记。
 * Excel 单元格里已经有字的字段，不再沿用侧车里的未填写标记。
 * 否则删除其他行、整表重写时，会把已经选好的案件类型写成空，内部案号也会被收回。
 * @param excelUnfilled Excel 读回时判定为空的字段
 * @param sidecarUnfilled 侧车记录的未填写字段
 * @param excelFilled Excel 单元格里已经有字的字段
 */
export function mergeLitigationUnfilledFields(
  excelUnfilled: string[] | undefined,
  sidecarUnfilled: string[] | undefined,
  excelFilled: string[] | undefined,
): string[] {
  const filled = new Set(excelFilled || []);
  const fromSidecar = (sidecarUnfilled || []).filter((field) => !filled.has(field));
  return [...new Set([...(excelUnfilled || []), ...fromSidecar])];
}

/**
 * 列表是否展示内部案号（尚未填写实质内容时不展示示意编号）。
 * @param item 案件
 */
export function litigationCaseShowsInternalNo(item: LitigationCase): boolean {
  if (!String(item.internalNo || '').trim() || !litigationInternalNoBasisReady(item)) return false;
  const title = String(item.title || '').trim();
  const caseName = String(item.caseName || '').trim();
  const courtNo = String(item.courtNo || '').trim();
  const claim = String(item.claimSummary || '').trim();
  const venue = String(item.venue || '').trim();
  return Boolean(title || caseName || courtNo || claim || venue);
}
